"use client"

import { useState, useCallback, useEffect, useRef } from "react"
import { useAuth } from "./use-auth"
import type { UnifiedCall, UnifiedCallStats, CallSource } from "@/types/unified-call"

const DEFAULT_CALLS_PAGE_SIZE = 25

export interface CallsFilters {
  search: string
  status: string
  agentId: string | null  // UUID
  source: CallSource
  callId: string | null
}

export interface CallsStatsExtended extends UnifiedCallStats {
  voiceAgentCalls: number
  pbxCalls: number
}

// Alias for backwards compatibility
export type CallsStats = CallsStatsExtended

const DEFAULT_FILTERS: CallsFilters = {
  search: "",
  status: "all",
  agentId: null,
  source: "all",
  callId: null,
}

const DEFAULT_STATS: CallsStatsExtended = {
  totalCalls: 0,
  completedCalls: 0,
  totalDurationMinutes: 0,
  totalCostMicrousd: 0,
  voiceAgentCalls: 0,
  pbxCalls: 0,
}

export const useCalls = (initialAgentId?: string | null, initialCallId?: string | null) => {
  const { fetchWithAuth, isAuthenticated } = useAuth()
  const [calls, setCalls] = useState<UnifiedCall[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [callsPage, setCallsPage] = useState(1)
  const [callsPageSize, setCallsPageSize] = useState(DEFAULT_CALLS_PAGE_SIZE)
  const [callsTotal, setCallsTotal] = useState(0)
  const [archivedCount, setArchivedCount] = useState(0)
  const [showArchived, setShowArchived] = useState(false)
  const [filters, setFilters] = useState<CallsFilters>({
    ...DEFAULT_FILTERS,
    agentId: initialAgentId ?? null,
    callId: initialCallId ?? null,
  })
  const [stats, setStats] = useState<CallsStatsExtended>(DEFAULT_STATS)

  // Debounce timer ref for search
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null)
  // Only the latest request may commit: a slower earlier one must not overwrite it.
  const latestRequestRef = useRef(0)

  // Load calls from database
  const loadCalls = useCallback(
    async (
      requestedPage: number,
      requestedPageSize: number,
      archived: boolean = false,
      currentFilters: CallsFilters = DEFAULT_FILTERS
    ) => {
      const pageSize = clampCallsPageSize(requestedPageSize, DEFAULT_CALLS_PAGE_SIZE)

      if (!isAuthenticated) {
        setCalls([])
        setCallsTotal(0)
        setCallsPage(1)
        setCallsPageSize(pageSize)
        setArchivedCount(0)
        setStats(DEFAULT_STATS)
        setIsLoading(false)
        setHasLoaded(true)
        return
      }

      const page = Math.max(1, Number.isFinite(requestedPage) ? Math.floor(requestedPage) : 1)
      const requestId = ++latestRequestRef.current
      const isLatest = () => requestId === latestRequestRef.current

      setIsLoading(true)
      try {
        const params = new URLSearchParams({
          page: String(page),
          pageSize: String(pageSize),
        })

        if (archived) {
          params.set("archived", "true")
        }

        // Add source filter
        if (currentFilters.source && currentFilters.source !== "all") {
          params.set("source", currentFilters.source)
        }

        // Add other filters
        if (currentFilters.search) {
          params.set("search", currentFilters.search)
        }
        if (currentFilters.status && currentFilters.status !== "all") {
          params.set("status", currentFilters.status)
        }
        if (currentFilters.agentId !== null) {
          params.set("agentId", String(currentFilters.agentId))
        }
        if (currentFilters.callId !== null) {
          params.set("callId", String(currentFilters.callId))
        }

        const response = await fetchWithAuth(`/api/calls?${params.toString()}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        })

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`)
        }

        const data = await response.json()
        if (!isLatest()) return
        const callsData: UnifiedCall[] = Array.isArray(data) ? data : (data.calls ?? [])
        setCalls(callsData)
        setCallsPage(data.page ?? page)
        setCallsPageSize(data.pageSize ?? pageSize)
        setCallsTotal(data.total ?? callsData.length)
        setArchivedCount(data.archivedCount ?? 0)
        setStats(data.stats ?? DEFAULT_STATS)
      } catch (error) {
        console.error("Error loading calls:", error)
        if (!isLatest()) return
        setCalls([])
        setCallsPage(page)
        setCallsPageSize(pageSize)
        setCallsTotal(0)
        setArchivedCount(0)
        setStats(DEFAULT_STATS)
      } finally {
        if (isLatest()) {
          setIsLoading(false)
          setHasLoaded(true)
        }
      }
    },
    [fetchWithAuth, isAuthenticated],
  )

  // Get call by id
  const getCall = useCallback(async (id: string) => {
    if (!isAuthenticated) {
      return null
    }

    try {
      const response = await fetchWithAuth(`/api/calls/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error("Error getting call:", error)
      return null
    }
  }, [fetchWithAuth, isAuthenticated])

  // Refresh calls
  const refreshCalls = useCallback(
    () => loadCalls(callsPage, callsPageSize, showArchived, filters),
    [loadCalls, callsPage, callsPageSize, showArchived, filters]
  )

  const goToCallsPage = useCallback(
    (page: number) => loadCalls(page, callsPageSize, showArchived, filters),
    [loadCalls, callsPageSize, showArchived, filters]
  )

  const changeCallsPageSize = useCallback(
    (pageSize: number) => loadCalls(1, pageSize, showArchived, filters),
    [loadCalls, showArchived, filters]
  )

  // Helper to cancel pending search debounce
  const cancelPendingSearch = useCallback(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current)
      searchDebounceRef.current = null
    }
  }, [])

  // Toggle archive view - always resets all filters
  const toggleArchived = useCallback((archived: boolean) => {
    cancelPendingSearch()
    setShowArchived(archived)
    // Reset all filters when entering or exiting archive mode
    const newFilters = DEFAULT_FILTERS
    setFilters(newFilters)
    loadCalls(1, callsPageSize, archived, newFilters)
  }, [loadCalls, callsPageSize, cancelPendingSearch])

  // Update search with debounce
  const updateSearch = useCallback((search: string) => {
    setFilters(prev => {
      const newFilters = { ...prev, search }

      // Clear existing debounce timer
      cancelPendingSearch()

      // Debounce the API call
      searchDebounceRef.current = setTimeout(() => {
        loadCalls(1, callsPageSize, showArchived, newFilters)
      }, 300)

      return newFilters
    })
  }, [loadCalls, callsPageSize, showArchived, cancelPendingSearch])

  // Update status filter (immediate)
  const updateStatus = useCallback((status: string) => {
    cancelPendingSearch()
    setFilters(prev => {
      const newFilters = { ...prev, status }
      loadCalls(1, callsPageSize, showArchived, newFilters)
      return newFilters
    })
  }, [loadCalls, callsPageSize, showArchived, cancelPendingSearch])

  // Update agent ID filter (immediate)
  const updateAgentId = useCallback((agentId: string | null) => {
    cancelPendingSearch()
    setFilters(prev => {
      const newFilters = { ...prev, agentId }
      loadCalls(1, callsPageSize, showArchived, newFilters)
      return newFilters
    })
  }, [loadCalls, callsPageSize, showArchived, cancelPendingSearch])

  // Update source filter (immediate)
  const updateSource = useCallback((source: CallSource) => {
    cancelPendingSearch()
    setFilters(prev => {
      const newFilters = { ...prev, source }
      loadCalls(1, callsPageSize, showArchived, newFilters)
      return newFilters
    })
  }, [loadCalls, callsPageSize, showArchived, cancelPendingSearch])

  // Clear all filters
  const clearFilters = useCallback(() => {
    cancelPendingSearch()
    const newFilters = DEFAULT_FILTERS
    setFilters(newFilters)
    loadCalls(1, callsPageSize, showArchived, newFilters)
  }, [loadCalls, callsPageSize, showArchived, cancelPendingSearch])

  // Check if any filters are active
  const hasActiveFilters = filters.search !== "" || filters.status !== "all" || filters.agentId !== null || filters.source !== "all" || filters.callId !== null

  // ---------- effect ----------
  useEffect(() => {
    const newFilters = {
      ...DEFAULT_FILTERS,
      agentId: initialAgentId ?? null,
      callId: initialCallId ?? null,
    }
    setFilters(newFilters)
    void loadCalls(1, DEFAULT_CALLS_PAGE_SIZE, false, newFilters)
  }, [loadCalls, initialAgentId, initialCallId])

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current)
      }
    }
  }, [])

  // ---------- api ----------
  return {
    calls,
    isLoading,
    // Only the first load has no list to keep on screen; later loads refresh it in place.
    isInitialLoading: isLoading && !hasLoaded,
    callsPage,
    callsPageSize,
    callsTotal,
    archivedCount,
    showArchived,
    filters,
    stats,
    hasActiveFilters,
    getCall,
    refreshCalls,
    goToCallsPage,
    changeCallsPageSize,
    toggleArchived,
    updateSearch,
    updateStatus,
    updateAgentId,
    updateSource,
    clearFilters,
  }
}

function clampCallsPageSize(value: number, fallback: number) {
  const size = Number(value)
  if (!Number.isFinite(size) || size <= 0) {
    return fallback
  }
  return Math.min(Math.max(Math.floor(size), 1), 100)
}
